import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  CtaButton,
  DetailField,
  DetailFieldList,
} from '@tassomutuo-backoffice/ui';

export interface AcquisitionReviewSection {
  title: string;
  fields: DetailField[];
  /** Plain-language consequence of the choices above, when there is one. */
  note?: string;
}

interface AcquisitionConfirmDialogProps {
  open: boolean;
  title: string;
  /** What confirming sets in motion, in the product's own terms. */
  notice: string;
  sections: AcquisitionReviewSection[];
  pending: boolean;
  onEdit: () => void;
  onConfirm: () => void;
}

const TITLE_ID = 'acquisition-confirm-title';
const NOTICE_ID = 'acquisition-confirm-notice';

/**
 * The read-only gate in front of an acquisition command: the operator sees the
 * compiled draft as entered and confirming is the only thing that reaches the
 * API. Nothing here is editable, so a correction always goes back to the form.
 *
 * Built on Material UI's `Dialog` rather than on the shared `ModalContainer`:
 * this is the last stop before an irreversible write, so it has to carry the
 * `dialog` role, an accessible name and description, and no dismissal chrome
 * competing with `Modifica dati`. `ModalContainer` offers a close icon and a
 * `presentation` root instead.
 */
const AcquisitionConfirmDialog = ({
  open,
  title,
  notice,
  sections,
  pending,
  onEdit,
  onConfirm,
}: AcquisitionConfirmDialogProps) => (
  <Dialog
    open={open}
    // A pending command owns the dialog: Esc and the backdrop must not leave
    // the operator without the pending state of the request they started.
    onClose={() => {
      if (!pending) onEdit();
    }}
    scroll="paper"
    fullWidth
    maxWidth="sm"
    aria-labelledby={TITLE_ID}
    aria-describedby={NOTICE_ID}
    PaperProps={{ 'aria-modal': true }}
  >
    <DialogTitle id={TITLE_ID} variant="h6">
      {title}
    </DialogTitle>
    <DialogContent dividers>
      <Stack spacing={3}>
        <Alert id={NOTICE_ID} severity="info">
          {notice}
        </Alert>
        {sections.map((section) => (
          <Stack key={section.title} spacing={1}>
            <Typography variant="subtitle1" component="h3" fontWeight="bold">
              {section.title}
            </Typography>
            <Divider />
            <DetailFieldList fields={section.fields} />
            {section.note && (
              <Typography variant="body2" color="text.secondary">
                {section.note}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2, gap: 2 }}>
      {/* The review opens on the way back to the form: a keyboard operator
          lands on the safe action, never on the irreversible one. */}
      <CtaButton
        type="button"
        variant="text"
        autoFocus
        onClick={onEdit}
        disabled={pending}
      >
        Modifica dati
      </CtaButton>
      <CtaButton
        type="button"
        variant="contained"
        onClick={onConfirm}
        loading={pending}
        disabled={pending}
      >
        Conferma e acquisisci lead
      </CtaButton>
    </DialogActions>
  </Dialog>
);

export default AcquisitionConfirmDialog;
